import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PublicBlogService } from '../../../_services/api/public/public-blog.service';
import { BlogDetail } from '../../../_models/blogs/blog.model';
import { ApiResponse } from '../../../_models/api-response.model';
import { Meta, Title } from '@angular/platform-browser';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';


@Component({
  selector: 'app-public-blog-details',
  imports: [CommonModule],
  templateUrl: './public-blog-details.html',
  styleUrl: './public-blog-details.scss',
})
export class PublicBlogDetails implements OnInit {
  blog: BlogDetail | null = null;
  isLoading: boolean = true;
  error: string | null = null;
  sanitizedContent: SafeHtml = '';

  socialLinks = [
    { name: 'facebook', icon: 'assets/images/icons/social/facebook.png', url: '#' },
    { name: 'x', icon: 'assets/images/icons/social/x.png', url: '#' },
    { name: 'instagram', icon: 'assets/images/icons/social/instagram.png', url: '#' },
    { name: 'linkedin', icon: 'assets/images/icons/social/linkedin.png', url: '#' },
    { name: 'gmail', icon: 'assets/images/icons/social/google.png', url: '#' }
  ];

   constructor(
    private route: ActivatedRoute,
    private router: Router,
    private publicBlogService: PublicBlogService,
    private sanitizer: DomSanitizer, 
    private title: Title,
    private meta: Meta,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}


  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const slug = params.get('slug') || params.get('id');
      
      if (slug) {
        this.loadBlogDetail(slug);
      } else {
        this.error = 'Identifiant du blog non trouvé';
        this.isLoading = false;
      }
    });
  }

  private loadBlogDetail(slug: string) {
    this.isLoading = true;
    this.error = null;

    this.publicBlogService.getPublicBlogDetail(slug).subscribe({
      next: (response: ApiResponse<BlogDetail>) => {
        if (response.success && response.data) {
          this.blog = response.data;
          this.setMetaTags();
        } else {
          this.error = 'Blog non trouvé';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading blog detail:', error);
        this.error = "Erreur lors du chargement d'article";
        this.isLoading = false;
      }
    });
  }

  hasBlogData(): boolean {
    return this.blog !== null && !this.isLoading;
  }

  setDefaultImage(event: any): void {
    event.target.src = './assets/images/blog-default.png';
  }

  // Meta data
  private setMetaTags(): void {
    if (!this.blog) return;
    const appTitle = 'Farco Security';
    const blogImage = this.getBlogMetaImage();
    const blogDescription = this.blog.excerpt || this.truncateText(this.blog.content, 160);
    
    this.title.setTitle(`${this.blog.title} | ${appTitle}`);

    this.meta.updateTag({ name: 'description', content: blogDescription });

    this.meta.updateTag({ property: 'og:type', content: 'article' });
    this.meta.updateTag({ property: 'og:title', content: this.blog.title });
    this.meta.updateTag({ property: 'og:description', content: blogDescription });
    this.meta.updateTag({ property: 'og:image', content: blogImage });
    this.meta.updateTag({ property: 'og:url', content: this.getCurrentUrl() });
    this.meta.updateTag({ property: 'og:site_name', content: appTitle });

    this.meta.updateTag({ property: 'article:published_time', content: this.blog.createdAt });
  }

  private getBlogMetaImage(): string {
    if (this.blog?.blogFiles && this.blog.blogFiles.length > 0) {
      const imageFile = this.blog.blogFiles.find(file => 
        file.mimeType.startsWith('image/') && file.isMain
      );
      if (imageFile) {
        return imageFile.location;
      }else{
         if (isPlatformBrowser(this.platformId)) {
          return `${window.location.origin}/assets/images/blog-details-default.png`;
        }
      }
    }
    if (isPlatformBrowser(this.platformId)) {
      return `${window.location.origin}/assets/images/blogs/blog-details-default.png`;
    }

    return '';
  }

  private getCurrentUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.href;
    }
    return '';
  }

  private truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }
}
